import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { shouldHideNavbar, NAV_ITEMS } from '../../constants/navigation';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { Logo } from '../ui/Logo';
import NotificationBell from '../../features/notifications/NotificationBell';

const DesktopSidebar: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const flags = useFeatureFlags();

  // H-88: filter out flag-gated nav items the user shouldn't see.
  const navItems = NAV_ITEMS.filter((item) => !item.requiresFlag || flags[item.requiresFlag]);

  const initials = useMemo(() => {
    const n = user?.displayName || '';
    return (
      n
        .split(' ')
        .map((s: string) => s[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || '?'
    );
  }, [user]);

  if (shouldHideNavbar(location.pathname)) return null;

  return (
    <div
      className='hidden md:flex flex-col flex-shrink-0 h-screen sticky top-0 border-r border-subtle md:w-[60px] lg:w-[220px] md:px-[10px] lg:px-[14px] py-[24px] transition-all'
      style={{ background: '#0c0c17' }}
    >
      {/* Logo */}
      <Logo
        size='sm'
        wordmarkClassName='hidden lg:inline text-[#E8EAF0]'
        className='mb-8 md:justify-center lg:justify-start md:px-0 lg:px-[6px]'
      />

      {/* Nav items */}
      <nav className='flex flex-col gap-0.5'>
        {navItems.map((item) => {
          const isRoom = item.path === '/events' && location.pathname.startsWith('/room/');
          const isActive =
            isRoom ||
            (item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path));

          return (
            <NavLink
              key={item.name}
              to={item.path}
              end={item.exact}
              title={item.name}
              className='flex items-center md:justify-center lg:justify-start gap-2.5 md:px-0 lg:px-3 py-[10px] rounded-[10px] text-[13px] transition-all'
              style={
                isActive
                  ? {
                      background: 'rgba(45,212,191,0.1)',
                      color: 'var(--color-primary)',
                      fontWeight: 600,
                    }
                  : { color: 'var(--color-text-muted)', fontWeight: 500 }
              }
            >
              <item.Icon
                className='w-[18px] h-[18px] flex-shrink-0'
                strokeWidth={isActive ? 2.5 : 2}
              />
              <span className='hidden lg:inline'>{item.name}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User avatar (md: icon only; lg: full card) */}
      <div className='mt-auto'>
        {/* M-377: Notification bell — md: centred icon, lg: left-aligned */}
        <div className='mb-2 flex md:justify-center lg:justify-start md:px-0 lg:px-[6px]'>
          <NotificationBell />
        </div>
        {/* md icon rail — avatar only */}
        <div className='md:flex lg:hidden justify-center'>
          <div
            className='w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0'
            style={{ background: 'linear-gradient(135deg,#818cf8,#6366f1)' }}
          >
            {initials}
          </div>
        </div>
        {/* lg full card */}
        <div
          className='hidden lg:flex items-center gap-2 p-[10px] rounded-[8px] px-[2px]'
          style={{ background: 'rgba(255,255,255,0.04)' }}
        >
          <div
            className='w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0'
            style={{ background: 'linear-gradient(135deg,#818cf8,#6366f1)' }}
          >
            {initials}
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-[11px] font-semibold text-main truncate leading-tight'>
              {user?.displayName || 'You'}
            </p>
            <p className='text-2xs text-dim truncate'>{user?.email || ''}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopSidebar;
